import React from "react";
import { Icon, EmptyState, notify, quotePayments, AutopilotStrip, nextTouch } from "./registry";
import { inFollowUp, sendsOf, lastSentOf } from "./lib/quotestate";
import { duePlans, CARE_LABEL } from "./lib/careplans";   // a re-seal / re-caulk coming due (§13 item 15)
import { localYmd } from "./lib/dates";
/* ============================================================
   Fence Works — To-Do
   One cross-customer "what needs to get done" list. It rolls up
   the open follow-up tasks you log on each customer (the dated
   ones especially) PLUS the money-on-the-line items that already
   drive each customer's "Needs attention" feed — quotes awaiting
   a reply, jobs to schedule, supplies to order, balances due —
   so the whole shop has one prioritized worklist. Surfaced both
   on the Dashboard and as its own page.
   ============================================================ */
const TD_money0 = window.VFP.money0;

// midnight-aligned day delta so "today / overdue" never drifts by hours
function TD_dayDelta(due: number) {
  const a = new Date(due); a.setHours(0, 0, 0, 0);
  const b = new Date(); b.setHours(0, 0, 0, 0);
  return Math.round((a.getTime() - b.getTime()) / 86400000);
}
function TD_dueMeta(due: number | null) {
  if (due == null) return null;
  const d = TD_dayDelta(due);
  if (d < 0) return { text: d === -1 ? "Yesterday" : `${-d} days overdue`, tone: "overdue" };
  if (d === 0) return { text: "Today", tone: "today" };
  if (d === 1) return { text: "Tomorrow", tone: "soon" };
  if (d <= 7) return { text: new Date(due).toLocaleDateString([], { weekday: "long" }), tone: "soon" };
  return { text: new Date(due).toLocaleDateString([], { month: "short", day: "numeric" }), tone: "later" };
}
const TD_TONE: any = { overdue: "var(--red)", today: "var(--amber)", soon: "var(--teal)", later: "var(--fg-3)" };

/* ---- manual to-dos: free-form items typed straight into this page ----
   Stored in settings.manualTodos (rides the workspace settings blob, so they
   sync across devices like every other setting). Completing keeps the record
   (with doneAt) so a mis-tap is recoverable; anything done 30+ days is pruned
   on the next write to keep the blob lean. */
function manualTodosOf(store: any) {
  return (store.settings && Array.isArray(store.settings.manualTodos)) ? store.settings.manualTodos : [];
}
function writeManualTodos(store: any, next: any[]) {
  const cutoff = Date.now() - 30 * 86400000;
  store.setSettings({ manualTodos: next.filter((t: any) => !t.done || (t.doneAt || 0) > cutoff) });
}
function addManualTodo(store: any, text: string, due: string | null) {
  const t = { id: "mt-" + Date.now() + "-" + Math.floor(Math.random() * 1000), text: text.trim(), due: due || null, done: false, createdAt: Date.now() };
  if (!t.text) return;
  writeManualTodos(store, [...manualTodosOf(store), t]);
}
function setManualTodoDone(store: any, id: string, done: boolean) {
  writeManualTodos(store, manualTodosOf(store).map((t: any) => t.id === id ? { ...t, done, doneAt: done ? Date.now() : undefined } : t));
}
function deleteManualTodo(store: any, id: string) {
  writeManualTodos(store, manualTodosOf(store).filter((t: any) => t.id !== id));
}

// the single source of truth for "what needs doing" across the workspace
function buildTodos(store: any) {
  const customers = store.customers || [];
  const quotes = store.quotes || [];
  const workOrders = store.workOrders || [];
  const items: any[] = [];

  // 0) manual to-dos added right on this page
  manualTodosOf(store).filter((t: any) => !t.done).forEach((t: any) => {
    items.push({
      id: "manual:" + t.id, kind: "manual", icon: "clipboard",
      title: t.text, customer: "Your to-do",
      due: t.due ? Date.parse(t.due + "T00:00:00") : null,
      value: 0, manual: { id: t.id }, go: null,
    });
  });

  // 1) open follow-up tasks logged on each customer (the dated-task ask)
  customers.forEach((c: any) => (c.tasks || []).filter((t: any) => !t.done).forEach((t: any) => {
    items.push({
      id: "task:" + c.id + ":" + t.id, kind: "task", icon: "clipboard",
      title: t.text, customer: c.name || "Customer",
      due: t.due ? Date.parse(t.due + "T00:00:00") : null,
      value: 0, task: { cid: c.id, id: t.id }, go: ["customers", { focus: c.id }],
    });
  }));

  // 1b) care plans coming due (lib/careplans.ts): a re-seal or re-caulk inside
  //     45 days — overdue first — booked in one tap from the customer's page
  duePlans(customers, localYmd(new Date()), 45).forEach(({ customer: c, plan: p }: any) => {
    items.push({
      id: "care:" + c.id + ":" + p.id, kind: "careplan", icon: "calendar",
      title: (CARE_LABEL[p.jobType] || "Care visit") + " due — book it", customer: c.name || "Customer",
      due: Date.parse(p.nextDue + "T00:00:00"), value: 0, go: ["customers", { focus: c.id }],
    });
  });

  // 2) quotes awaiting a response — and online booking / consult requests, which
  // come in as pending quotes (source "booking") carrying a requested date so
  // they surface here as something to schedule, dated by the customer's pick.
  // EST-02: only quotes that were actually SENT are awaiting a response. A
  // draft nobody has sent is work to finish, not a customer gone quiet, and it
  // used to be chased because creation seeded a send.
  quotes.filter((q: any) => (q.status === "pending" || q.status === "viewed") && (inFollowUp(q) || q.source === "booking")).forEach((q: any) => {
    const booking = q.source === "booking" ? (q.booking || {}) : null;
    const reqDate = booking && booking.requestedDate ? Date.parse(booking.requestedDate + "T00:00:00") : null;
    // follow-up urgency: touches so far + days since the last one went out
    const sends = sendsOf(q);
    const lastAt = lastSentOf(q) || Number(q.createdAt) || 0;
    const daysSince = lastAt ? Math.floor((Date.now() - lastAt) / 86400000) : null;
    const touches = sends.length;
    const isNew = !booking && touches <= 1 && daysSince != null && daysSince <= 0;
    items.push({
      id: "quote:" + q.id, kind: booking ? "schedule" : "quote", icon: booking ? "calendar" : "send",
      title: booking ? "Consult request — schedule it" : isNew ? "New lead — reply fast" : "Quote awaiting response",
      customer: q.customer || "Customer", due: reqDate && !isNaN(reqDate) ? reqDate : null,
      value: q.total || 0, go: ["quote", { quoteId: q.id }],
      // one-tap follow-up payload (manual layer over the speed-to-lead autopilot)
      followup: booking ? null : { quoteId: q.id, customerId: q.customerId, name: q.customer, phone: q.phone, email: q.email, total: q.total, touches, daysSince, isNew },
    });
  });

  // 3) jobs to schedule / supplies to order
  workOrders.filter((w: any) => w.status === "unscheduled").forEach((w: any) =>
    items.push({ id: "wo-sched:" + w.id, kind: "schedule", icon: "calendar", title: "Job needs scheduling",
      customer: w.customer || w.title || "Job", due: null, value: w.total || 0, go: ["workorders", null] }));
  workOrders.filter((w: any) => w.status === "materials").forEach((w: any) =>
    items.push({ id: "wo-mat:" + w.id, kind: "supplies", icon: "box", title: "Supplies to order",
      customer: w.customer || w.title || "Job", due: null, value: w.total || 0, go: ["workorders", null] }));

  // 4) balances still owed on booked jobs (paid = deposit/full; signed with a
  //    partial payment recorded stays "signed" until the balance clears)
  quotes.filter((q: any) => q.status === "paid" || (q.status === "signed" && quotePayments(q).paid > 0)).forEach((q: any) => {
    const bal = Math.max(0, (q.total || 0) - quotePayments(q).paid);   // Stripe deposits + manual payments
    if (bal > 0) items.push({ id: "bal:" + q.id, kind: "balance", icon: "dollar", title: "Balance due",
      customer: q.customer || "Customer", due: null, value: bal, go: ["quote", { quoteId: q.id }] });
  });

  // dated items first (overdue → soonest), then the rest by $ on the line
  items.sort((a, b) => {
    if (a.due != null && b.due != null) return a.due - b.due;
    if (a.due != null) return -1;
    if (b.due != null) return 1;
    return (b.value || 0) - (a.value || 0);
  });
  return items;
}
// how many are urgent (overdue or due today) — drives the nav badge
function todoUrgentCount(store: any) {
  return buildTodos(store).filter(t => t.due != null && TD_dayDelta(t.due) <= 0).length;
}

/* one-tap follow-up: the service sends the message and logs the touch on the
   quote. Falls back to a prefilled sms:/mailto: hand-off when there's no live
   service or the send can't go through — so it works offline, and it never
   claims to have sent something it didn't. The human override on top of the
   speed-to-lead autopilot. */
function buildFollowupMsg(store: any, f: any) {
  const co = (store.company && store.company.name) || (store.settings && store.settings.companyName) || "us";
  const first = String(f.name || "there").trim().split(/\s+/)[0] || "there";
  const amt = f.total ? ` (${TD_money0(f.total)})` : "";
  if (f.isNew) return `Hi ${first}, thanks for reaching out to ${co}! We got your fence request${amt} and can get you a firm number fast. Is now a good time for a couple quick questions, or want to grab a visit this week?`;
  return `Hi ${first}, following up on your fence estimate${amt} from ${co} — happy to answer any questions or get you on the schedule. Want me to hold a spot this week?`;
}
/* FW-024: this fired quote.followup.manual, a trigger with no listener — and
   dispatchEvent drops any automation whose toggle is off anyway, so pressing
   the button deliberately still sent nothing. It now asks the service to send
   it and says what happened; the sms:/mailto: hand-off stays as the fallback
   for a workspace with no live service (or no SMS/email configured). */
async function doFollowup(store: any, go: any, f: any) {
  const msg = buildFollowupMsg(store, f);
  const DB: any = (window as any).VFP_DB;
  if (DB && DB.messageSendNow && DB.apiBase && DB.apiBase() && f.customerId) {
    try {
      const r = await DB.messageSendNow({ kind: "quote_followup", customerId: f.customerId, amount: f.total || 0, body: msg });
      if (r && r.deferred) notify(`Queued for ${f.name || "the customer"} — it goes out when quiet hours lift.`);
      else notify(`Follow-up sent to ${f.name || "the customer"}${r && r.downgraded ? " by email (they haven't opted in to texts)" : ""}.`);
      try { store.logQuoteSend(f.quoteId); } catch (e) { /* logging is best-effort */ }
      store.addActivity(`Followed up with ${f.name || "customer"}`);
      return;
    } catch (e: any) {
      // fall through to the hand-off below, but SAY why rather than pretending
      notify(`Couldn't send it for you (${String((e && e.message) || e)}) — opening your own app instead.`, { kind: "error" });
    }
  }
  if (f.phone) window.open(`sms:${String(f.phone).replace(/[^\d+]/g, "")}?&body=${encodeURIComponent(msg)}`);
  else if (f.email) window.open(`mailto:${f.email}?subject=${encodeURIComponent("Following up on your fence estimate")}&body=${encodeURIComponent(msg)}`);
  else { go("quote", { quoteId: f.quoteId }); return; }
  try { store.logQuoteSend(f.quoteId); } catch (e) { /* logging is best-effort */ }
  store.addActivity(`Followed up with ${f.name || "customer"}`);
}

// one row — used on the page and (compact) on the dashboard
function TodoRow({ item, store, go, compact }: any) {
  const due = TD_dueMeta(item.due);
  const toggle = () => {
    if (item.kind === "manual") { setManualTodoDone(store, item.manual.id, true); return; }
    const c = (store.customers || []).find((x: any) => x.id === item.task.cid);
    if (!c) return;
    store.updateCustomer(c.id, { tasks: (c.tasks || []).map((t: any) => t.id === item.task.id ? { ...t, done: true, doneAt: Date.now() } : t) });
  };
  const open = () => { if (item.go) go(item.go[0], item.go[1]); };
  return (
    <div className={"todo-row" + (due && due.tone === "overdue" ? " overdue" : "")}>
      {item.kind === "task" || item.kind === "manual"
        ? <button className="ready-check" onClick={toggle} title="Mark done" aria-label="Mark done" />
        : <span className="attn-ic" style={{ width: 26, height: 26 }}><Icon name={item.icon} size={13} /></span>}
      <button className="todo-main" onClick={open} style={item.go ? undefined : { cursor: "default" }}>
        <span className="todo-title">{item.title}</span>
        <span className="todo-sub">{item.customer}</span>
        {/* the autopilot's next move on this lead, so the owner knows whether to step in */}
        {!compact && item.followup && (() => { const n = nextTouch(store, item.followup); return <span className={"todo-auto" + (n.on ? " on" : "")}><Icon name="bolt" size={10} />{n.text}</span>; })()}
      </button>
      {due && <span className="todo-due" style={{ color: TD_TONE[due.tone], borderColor: due.tone === "overdue" ? "var(--red-tint)" : "var(--line-2)" }}>{due.text}</span>}
      {!due && item.value > 0 && <span className="tnum todo-val">{TD_money0(item.value)}</span>}
      {item.followup && item.followup.daysSince != null && item.followup.daysSince >= 1 &&
        <span className="fu-age" style={{ color: item.followup.daysSince >= 3 ? "var(--red)" : "var(--amber)" }}>{item.followup.daysSince}d cold</span>}
      {item.followup
        ? <button className="fu-btn" title="Text or email a follow-up now" onClick={(e) => { e.stopPropagation(); doFollowup(store, go, item.followup); }}><Icon name="send" size={12} />{compact ? "" : "Follow up"}</button>
        : item.kind === "manual"
          ? (!compact && <button className="btn btn-ghost btn-sm" title="Delete this to-do" style={{ padding: "4px 7px", flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); deleteManualTodo(store, item.manual.id); }}><Icon name="trash" size={13} /></button>)
          : (!compact && <Icon name="chevright" size={15} className="muted" style={{ flexShrink: 0 }} />)}
    </div>
  );
}

// compact list for the Dashboard "Needs Attention" card
function DashTodos({ store, go }: any) {
  const todos = buildTodos(store);
  if (todos.length === 0)
    return <div className="chip green" style={{ width: "100%", justifyContent: "center", padding: "8px" }}><Icon name="check" size={14} />All caught up</div>;
  const top = todos.slice(0, 5);
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {top.map(t => <TodoRow key={t.id} item={t} store={store} go={go} compact />)}
      <button className="btn btn-ghost btn-sm" style={{ width: "100%", marginTop: 4 }} onClick={() => go("todo")}>
        {todos.length > 5 ? `View all ${todos.length} →` : "Open To-Do →"}
      </button>
    </div>
  );
}

/* the add-a-to-do composer: type it, optionally date it, hit Add (or Enter) */
function TodoComposer({ store }: any) {
  const [text, setText] = React.useState<any>("");
  const [due, setDue] = React.useState<any>("");
  const add = () => {
    if (!text.trim()) return;
    addManualTodo(store, text, due || null);
    setText(""); setDue("");
  };
  return (
    <div className="card pad" style={{ marginTop: 14 }}>
      <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
        <input className="input" style={{ flex: "2 1 240px" }} value={text} placeholder="Add a to-do — “order pickets for Miller job”, “call the county about permits”…"
          onChange={(e: any) => setText(e.target.value)}
          onKeyDown={(e: any) => { if (e.key === "Enter") add(); }} />
        <input className="input" type="date" style={{ width: 150 }} value={due} title="Due date (optional)" onChange={(e: any) => setDue(e.target.value)} />
        <button className="btn btn-primary btn-sm" disabled={!text.trim()} onClick={add}><Icon name="plus" size={13} />Add</button>
      </div>
    </div>
  );
}

// full page — grouped by urgency
function TodoPage({ store, go }: any) {
  const todos = buildTodos(store);
  const groups: any[] = [
    { key: "overdue", label: "Overdue", items: todos.filter(t => t.due != null && TD_dayDelta(t.due) < 0) },
    { key: "today", label: "Today", items: todos.filter(t => t.due != null && TD_dayDelta(t.due) === 0) },
    { key: "upcoming", label: "Upcoming", items: todos.filter(t => t.due != null && TD_dayDelta(t.due) > 0) },
    { key: "anytime", label: "No due date", items: todos.filter(t => t.due == null) },
  ].filter(g => g.items.length > 0);
  const taskCount = todos.filter(t => t.kind === "task" || t.kind === "manual").length;

  return (
    <div className="page fade-up" style={{ maxWidth: 760 }}>
      <div className="row between" style={{ marginBottom: 6, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 className="display" style={{ fontSize: 28, margin: 0 }}>To-Do</h1>
          <p className="muted" style={{ margin: "4px 0 0", fontSize: 14 }}>Everything that needs doing across your customers and jobs.</p>
        </div>
        <button className="btn btn-outline btn-sm" onClick={() => go("customers")}><Icon name="user" size={14} />Customers</button>
      </div>

      {(() => {
        const fu = todos.filter((t: any) => t.followup);
        if (!fu.length) return null;
        const cold = fu.filter((t: any) => t.followup.daysSince != null && t.followup.daysSince >= 3).length;
        return (
          <div className="fu-banner">
            <span className="fu-banner-ic"><Icon name="bolt" size={15} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{fu.length} open lead{fu.length === 1 ? "" : "s"} to follow up{cold ? ` · ${cold} going cold` : ""}</div>
              <div className="muted" style={{ fontSize: 12 }}>Tap <b style={{ color: "var(--fg-2)" }}>Follow up</b> to text or email now. Speed-to-lead &amp; the multi-touch cadence run automatically once your AI Front Desk is connected.</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => go("ai")}><Icon name="bolt" size={13} />Autopilot</button>
          </div>
        );
      })()}
      <AutopilotStrip store={store} go={go} />

      <TodoComposer store={store} />

      {todos.length === 0
        ? <div className="card pad" style={{ marginTop: 16 }}>
            <EmptyState icon="check" title="All caught up" sub="Type a to-do above to add your own, or add a follow-up from any customer's Notes tab. Quotes awaiting a reply, jobs to schedule and balances due collect here automatically." />
          </div>
        : <div style={{ display: "grid", gap: 16, marginTop: 14 }}>
            {groups.map(g => (
              <div key={g.key} className="card pad">
                <div className="row between" style={{ marginBottom: 12 }}>
                  <span className={"kicker" + (g.key === "overdue" ? "" : " kicker-teal")} style={g.key === "overdue" ? { color: "var(--red)" } : undefined}>
                    {g.key === "overdue" && <Icon name="bolt" size={13} />}{g.label}
                  </span>
                  <span className="muted" style={{ fontSize: 12 }}>{g.items.length}</span>
                </div>
                <div style={{ display: "grid", gap: 7 }}>
                  {g.items.map((t: any) => <TodoRow key={t.id} item={t} store={store} go={go} />)}
                </div>
              </div>
            ))}
            <p className="faint" style={{ fontSize: 12, margin: "0 2px" }}>
              {taskCount} follow-up{taskCount === 1 ? "" : "s"} · tick one off to clear it. Add new follow-ups from a customer's Notes tab.
            </p>
          </div>}
    </div>
  );
}

Object.assign(window, { buildTodos, todoUrgentCount, TodoPage, DashTodos });

export { buildTodos, todoUrgentCount, TodoPage, DashTodos, TodoRow };
